import { DollarOutlined } from "@ant-design/icons";
import { Button, Checkbox, message, Modal, Select, Space, Tooltip } from "antd"
import { useCallback, useEffect, useMemo, useState } from "react";
import { serverFetch } from "../../server";
import { fmtTime, RECORD_PASS_VID, resolveRecordPassVid } from "../../extensions/utils";
import Client from "../clients/client";
import dayjs from "dayjs";

const getClientCnt = (record) => record.cnts.find((c) => c.objectVid === 1);

const getEffectivePassVid = (record) => {
  const client = getClientCnt(record);
  return resolveRecordPassVid(client?.passVid, record.passVid);
};

/** Оформление визита по смартзаписи */
const RecordSmartVisit = ({onClose, onRefresh, items}) => {
  const unusedItems = useMemo(
    () => items.filter((i) => !i.cnts.find((c) => c.objectVid === 1 && c.used)),
    [items]
  );

  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [abon, setAbon] = useState(null);
  const [abons, setAbons] = useState([]);
  const [packetAbon, setPacketAbon] = useState(null);
  const [packetAbons, setPacketAbons] = useState([]);
  const [editClient, setEditClient] = useState(false);

  const client = getClientCnt(items[0]);

  // Автоматический выбор записей при загрузке, если все они однотипные
  useEffect(() => {
    if (selected.length > 0) return; // пользователь уже выбрал
    if (!unusedItems.length) return;

    const firstVid = getEffectivePassVid(unusedItems[0]);
    const allSame = unusedItems.every((i) => getEffectivePassVid(i) === firstVid);
    if (allSame) {
      const defaultSelection = unusedItems.map((i) => getClientCnt(i).id);
      setSelected(defaultSelection);
    }
  }, [unusedItems, selected.length]);

  const selectedItems = useMemo(
    () => items.filter((i) => {
      const c = getClientCnt(i);
      return c && selected.includes(c.id);
    }),
    [items, selected]
  );

  const selectedPassVids = useMemo(
    () => [...new Set(selectedItems.map((i) => getEffectivePassVid(i)))],
    [selectedItems]
  );

  const mode = useMemo(() => {
    if (selectedPassVids.length === 1) return selectedPassVids[0];
    return null;
  }, [selectedPassVids]);

  const needsAbonement = mode === RECORD_PASS_VID.ByAbon;
  const needsPacket = mode === RECORD_PASS_VID.ByPacket;
  const selectionMixed = selectedPassVids.length > 1;

  // Получение списка ID продуктов (услуг) из выбранных записей (нужно для проверки пакетов)
  const productIdsInSelected = useMemo(() => {
    const ids = [];
    selectedItems.forEach(record => {
      record.cnts.filter(c => c.objectVid === 2).forEach(p => ids.push(p.objectId));
    });
    return ids;
  }, [selectedItems]);

  const loadAbons = useCallback(() => {
    if (!needsAbonement) return;
    serverFetch(`sale/ListByClient/${client.objectId}?vids=2`)
      .then((data) => {
        const newAbons = data.filter((i) => i.status === 2 && !i.deleted).map((i) => ({ label: `[${i.id}] ${i.productName}`, value: i.id }));
        setAbons(newAbons);
        setAbon((prev) => {
          if (newAbons.length === 0) return null;
          if (newAbons.some((a) => a.value === prev)) return prev;
          return newAbons[0].value;
        });
      })
      .catch((e) => message.error(e.userMessage));
  }, [needsAbonement, client.objectId]);

  // Загрузка абонементов с пакетами для ByPacket
  const loadPacketAbons = useCallback(() => {
    if (!needsPacket) return;

    serverFetch(`Sale/ClientPackets/${client.objectId}`, { method: "GET" })
      .then((data) => {
        const activePacketSales = data.filter(
          (sale) => sale.status === 2 && !sale.deleted && !sale.blocked
        );

        if (activePacketSales.length === 0) {
          setPacketAbons([]);
          setPacketAbon(null);
          return;
        }

        const eligibleAbons = [];
        
        const processNext = (index) => {
          if (index >= activePacketSales.length) {
            setPacketAbons(eligibleAbons);
            setPacketAbon((prev) => {
              if (eligibleAbons.length === 0) return null;
              if (prev != null && eligibleAbons.some((a) => a.value === prev)) return prev;
              return eligibleAbons[0].value;
            });
            return;
          }

          const sale = activePacketSales[index];
          serverFetch(`sale/ProductList/${sale.id}`, { method: "GET" })
            .then((products) => {
              const hasProduct = products.some(
                (p) => productIdsInSelected.includes(p.productId) && (p.qtyRest || 0) > 0
              );
              if (hasProduct) {
                eligibleAbons.push({
                  label: `[${sale.id}] ${sale.productName || `Абонемент`}`,
                  value: sale.id,
                });
              }
            })
            .catch(() => {}) // ошибка загрузки пакетов для одного абонемента — игнорируем
            .finally(() => processNext(index + 1));
        };

        processNext(0);
      })
      .catch((e) => {
        message.error(e.userMessage);
        setPacketAbons([]);
        setPacketAbon(null);
      });
  }, [needsPacket, client, productIdsInSelected]);

  // Вызов загрузки при смене режима
  useEffect(() => {
    if (mode === RECORD_PASS_VID.ByAbon) {
      loadAbons();
    } else if (mode === RECORD_PASS_VID.ByPacket) {
      loadPacketAbons();
    } else {
      // Сброс при смешанном режиме или при отсутствии выбора
      setAbons([]);
      setAbon(null);
      setPacketAbons([]);
      setPacketAbon(null);
    }
  }, [mode, loadAbons, loadPacketAbons]);

  // Функция, которая возвращает выбранные записи, совместимые с указанным видом прохода
  const filterSelectedByPassVid = (passVid) => {
    return selected.filter((sid) => {
      const r = items.find((i) => getClientCnt(i)?.id === sid);
      return r && getEffectivePassVid(r) === passVid;
    });
  };

  // Переключение выбора записи
  const toggleSelect = (id, checked) => {
    if (!checked) {
      setSelected(selected.filter((i) => i !== id));
      return;
    }

    const record = items.find((i) => getClientCnt(i)?.id === id);
    if (!record) return;

    const passVid = getEffectivePassVid(record);
    // При выборе новой записи мы сбрасываем все ранее выбранные записи,
    // если их вид прохода отличается от вида новой записи.
    // Это предотвращает смешивание разных видов в одном запросе.
    const samePassVidSelected = filterSelectedByPassVid(passVid);
    setSelected([...samePassVidSelected, id]);
  };

  // Сохранение (отправка запроса)
  const onSave = () => {
    if (selectionMixed || !selected.length) return;
    let saleId = null;
    if (mode === RECORD_PASS_VID.ByAbon) saleId = abon;
    else if (mode === RECORD_PASS_VID.ByPacket) saleId = packetAbon;
    setLoading(true);
    serverFetch("visit/AddByRecords", {
      method: "POST",
      bodyData: { recordCnts: selected, saleId },
    })
      .then(() => onClose(true))
      .catch((e) => {
        setLoading(false);
        message.error(e.userMessage);
        onRefresh?.();
        if (mode === RECORD_PASS_VID.ByAbon) {
          loadAbons();
        } else if (mode === RECORD_PASS_VID.ByPacket) {
          loadPacketAbons();
        }
      });
  };

  const divVisits = items.filter(i => !i.deleted).map(i => {
    const clientCnt = getClientCnt(i);
    const effective = getEffectivePassVid(i);
    const isUsed = i.cnts.find(c => c.objectVid === 1 && c.used);
    return <div className="record-visit-item" key={clientCnt.id}>
      <Checkbox checked={selected.includes(clientCnt.id)} onChange={e => toggleSelect(clientCnt.id, e.target.checked)} disabled={isUsed}>
        {fmtTime(i.start)}-{fmtTime(i.finish)} {i.cnts.filter(i2 => i2.objectVid === 2).map(i2 => i2.objectName).join(", ")}
        {` #${i.id}`}
        {effective === RECORD_PASS_VID.ByPayment && (
          <Tooltip title="За плату">
            <DollarOutlined style={{ marginLeft: 6, color: "#2f6f00", verticalAlign: "middle" }} />
          </Tooltip>
        )}
        {effective === RECORD_PASS_VID.ByPacket && (
          <span style={{ marginLeft: 6, color: "#8d8d8d", fontSize: 12 }}>пакет</span>
        )}
        {effective === RECORD_PASS_VID.ByAbon && (
          <span style={{ marginLeft: 6, color: "#8d8d8d", fontSize: 12 }}>абонемент</span>
        )}
      </Checkbox>
    </div>
  })  

  // Предупреждение по интервалу времени выбранных записей
  const timeWarning = useMemo(() => {
    const items2 = items.filter(i => selected.includes(getClientCnt(i)?.id));
    if (!items2?.length) return null;
    const minStart = items2.reduce((min, it) => (dayjs(it.start).isBefore(min) ? dayjs(it.start) : min), dayjs(items2[0].start));
    const maxFinish = items2.reduce((max, it) => (dayjs(it.finish).isAfter(max) ? dayjs(it.finish) : max), dayjs(items2[0].finish));
    const now = dayjs();
    if (now.isBefore(minStart)) return "Время записи еще не наступило";
    if (now.isAfter(maxFinish)) return "Время записи уже прошло";
    return null;
  }, [items, selected]);

  const isDisabled = !selected.length
    || selectionMixed
    || (needsAbonement && !abon)
    || (needsPacket && !packetAbon);

  return<Modal open onCancel={() => onClose()} footer={null} title="Визит по смартзаписи" loading={loading} closeIcon={!editClient}>
    <div className="record-visit-title">Клиент</div>
    <div className="record-visit-item"><Space><div>{client.objectName}</div><Button onClick={() => setEditClient(true)}>Изменить</Button></Space></div>
    {needsAbonement && (
      <>
        <div className="record-visit-title">Абонемент</div>
        <div className="record-visit-item"><Select style={{ width: "100%" }} options={abons} value={abon} onChange={setAbon} /></div>
      </>
    )}
    {needsPacket && (
      <>
        <div className="record-visit-title">Абонемент с пакетами</div>
        <div className="record-visit-item">
          <Select
            style={{ width: "100%" }}
            options={packetAbons}
            value={packetAbon}
            onChange={setPacketAbon}
            placeholder="Выберите абонемент"
          />
        </div>
      </>
    )}
    <div className="record-visit-title">Услуги</div>
    {divVisits}
    {selectionMixed && (
      <div className="record-visit-hint">По записи можно открыть один визит с видом либо за плату, либо за абонемент, либо за услуги пакета</div>
    )}
    {timeWarning && (
      <div style={{
        marginTop: 12,
        marginBottom: 12,
        padding: 8,
        borderRadius: 4,
        background: "#fffbe6",
        border: "1px solid #ffe58f",
        color: "#ad8b00"
      }}>{timeWarning}</div>
    )}
    <div className="record-visit-footer"><Button disabled={isDisabled} className="dolg-button" onClick={onSave}>Открыть визит</Button></div>
    {editClient && (
      <Client
        id={client.objectId}
        onClose={() => {
          setEditClient(false);
          if (mode === RECORD_PASS_VID.ByAbon) loadAbons();
          else if (mode === RECORD_PASS_VID.ByPacket) loadPacketAbons();
        }}
      />
    )}
  </Modal>
}

export default RecordSmartVisit;
